import React, { useMemo, useState } from "react";

import { Box, Typography, Button, Menu, MenuItem } from "@mui/material";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import DashboardTabFilter from "../reuseable-components/DashboardTabFilter";
import DashBoardTable from "../reuseable-components/DashBoardTable";

import {
  downloadCSV,
  downloadExcel,
  downloadPDF,
} from "../../../utils/exportUtils";

const formatSignupDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString("en-GB");
};

const EmailSignUpsTab = ({ signups = [] }) => {
  const columns = [
    {
      id: "email",
      label: "Email",
      sortable: true,
    },
    {
      id: "source",
      label: "Source",
      sortable: true,
    },
    {
      id: "date",
      label: "Date",
      sortable: true,
    },
  ];

  const allRows = useMemo(
    () =>
      signups.map((signup) => ({
        email: signup.email || "-",
        source: signup.source || "-",
        date: formatSignupDate(signup.createdAt),
      })),
    [signups],
  );

  const defaultFilters = {
    search: "",
    source: "All Sources",
    from_date: "",
    to_date: "",
  };

  const [filters, setFilters] = useState(defaultFilters);

  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);

  const [page, setPage] = useState(1);

  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "asc",
  });

  /* =========================
      EXPORT MENU
  ========================= */

  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  /* =========================
      FILTERS
  ========================= */

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);

    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters);

    setAppliedFilters(defaultFilters);

    setPage(1);
  };

  /* =========================
      SORTING
  ========================= */

  const handleSort = (columnId) => {
    setSortConfig((prev) => ({
      key: columnId,
      direction:
        prev.key === columnId && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  /* =========================
      FILTER + SORT DATA
  ========================= */

  const filteredRows = useMemo(() => {
    let data = [...allRows];

    // Search
    if (appliedFilters.search) {
      const search = appliedFilters.search.toLowerCase();

      data = data.filter((item) => item.email.toLowerCase().includes(search));
    }

    // SOURCE
    if (appliedFilters.source !== "All Sources") {
      data = data.filter((item) => item.source === appliedFilters.source);
    }

    // FROM DATE
    if (appliedFilters.from_date) {
      data = data.filter(
        (item) => new Date(item.date) >= new Date(appliedFilters.from_date),
      );
    }

    // TO DATE
    if (appliedFilters.to_date) {
      data = data.filter(
        (item) => new Date(item.date) <= new Date(appliedFilters.to_date),
      );
    }

    // SORTING
    if (sortConfig.key) {
      data.sort((a, b) => {
        const valueA = a[sortConfig.key];

        const valueB = b[sortConfig.key];

        if (valueA < valueB) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }

        if (valueA > valueB) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }

        return 0;
      });
    }

    return data;
  }, [allRows, appliedFilters, sortConfig]);

  /* =========================
      PAGINATION
  ========================= */

  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;

    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  return (
    <Box
      sx={{
        p: {
          xs: 1,
          md: 3,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#0F1E3D",
          }}
        >
          Email Signups
        </Typography>

        <>
          <Button
            variant="contained"
            endIcon={<ArrowDropDownIcon />}
            onClick={handleExportClick}
            sx={{
              bgcolor: "#C4912E",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Export
          </Button>

          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            <MenuItem
              onClick={() => {
                downloadCSV({
                  data: filteredRows,
                  fileName: "email-signups",
                });

                handleClose();
              }}
            >
              Download CSV
            </MenuItem>

            <MenuItem
              onClick={() => {
                downloadExcel({
                  data: filteredRows,
                  fileName: "email-signups",
                  sheetName: "Email Signups",
                });

                handleClose();
              }}
            >
              Download Excel
            </MenuItem>

            <MenuItem
              onClick={() => {
                downloadPDF({
                  data: filteredRows,
                  fileName: "email-signups",
                  title: "Email Signups",
                  columns,
                });

                handleClose();
              }}
            >
              Download PDF
            </MenuItem>
          </Menu>
        </>
      </Box>

      {/* Filters */}
      <DashboardTabFilter
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        showZipFilter={false}
        searchPlaceholder="Search email"
        sourceLabel="Source"
        sourceOptions={[
          { label: "All Sources", value: "All Sources" },
          { label: "Email Bar", value: "Email Bar" },
          { label: "Website Form", value: "Website Form" },
          { label: "Manual Test", value: "Manual Test" },
        ]}
      />

      {/* Table */}
      <DashBoardTable
        columns={columns}
        rows={paginatedRows}
        sortConfig={sortConfig}
        onSort={handleSort}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(value) => {
          setRowsPerPage(value);

          setPage(1);
        }}
        totalResults={filteredRows.length}
      />
    </Box>
  );
};

export default EmailSignUpsTab;
